<template>
  <sui-segment-group
    class="sat-certificate"
    raised
  >
    <sui-segment
      inverted
      class="section-title"
    >
      Certificado de Sello Digital (CSD)
    </sui-segment>

    <sui-segment>
      <sui-message info>
        Sube aquí tu Certificado de Sello Digital (SAT) para poder timbrar tus facturas.
        Los archivos <b>.cer</b> y <b>.key</b> y la contraseña se envían directamente a tu
        proveedor de timbrado y <b>no se almacenan en el servidor</b>. Solo tú los subes.
      </sui-message>

      <sui-form>
        <sui-form-field>
          <label>Archivo del certificado (.cer)</label>
          <input
            ref="cerInput"
            type="file"
            accept=".cer"
            @change="onCerChange"
          >
        </sui-form-field>

        <sui-form-field>
          <label>Archivo de la llave privada (.key)</label>
          <input
            ref="keyInput"
            type="file"
            accept=".key"
            @change="onKeyChange"
          >
        </sui-form-field>

        <sui-form-field>
          <label>Contraseña de la llave privada</label>
          <input
            v-model="password"
            type="password"
            placeholder="Contraseña del CSD"
            autocomplete="off"
          >
        </sui-form-field>
      </sui-form>
    </sui-segment>

    <sui-segment>
      <sui-message
        v-if="errorMessage"
        error
      >
        {{ errorMessage }}
      </sui-message>
      <sui-button
        positive
        :loading="saving"
        :disabled="saving"
        @click="save"
      >
        Registrar certificado
      </sui-button>
    </sui-segment>

    <sui-segment :loading="loading">
      <h4>Certificados registrados</h4>
      <sui-message
        v-if="!loading && certificates.length === 0"
        warning
      >
        Aún no hay certificados registrados.
      </sui-message>
      <table
        v-else-if="certificates.length"
        class="ui celled compact table"
      >
        <thead>
          <tr>
            <th>RFC</th>
            <th>Razón social</th>
            <th>No. Certificado</th>
            <th>Vigencia</th>
            <th>Activo</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(cert, idx) in certificates"
            :key="cert.certificate_number || idx"
          >
            <td>{{ cert.issuer_rfc || "—" }}</td>
            <td>{{ cert.issuer_business_name || "—" }}</td>
            <td>{{ cert.certificate_number || "—" }}</td>
            <td>{{ formatDate(cert.valid_from) }} → {{ formatDate(cert.valid_to) }}</td>
            <td>
              <sui-icon
                :name="cert.is_active ? 'check circle' : 'times circle'"
                :color="cert.is_active ? 'green' : 'red'"
              />
            </td>
          </tr>
        </tbody>
      </table>
    </sui-segment>
  </sui-segment-group>
</template>

<script setup>
import { onMounted, ref } from "vue";
import { useToast } from "vue-toastification";
import Api from "../../api/Api";

const toast = useToast();
const loading = ref(true);
const saving = ref(false);
const errorMessage = ref("");
const password = ref("");
const cerFile = ref(null);
const keyFile = ref(null);
const cerInput = ref(null);
const keyInput = ref(null);
const certificates = ref([]);

const onCerChange = (e) => {
    cerFile.value = e.target.files && e.target.files[0] ? e.target.files[0] : null;
};
const onKeyChange = (e) => {
    keyFile.value = e.target.files && e.target.files[0] ? e.target.files[0] : null;
};

// Read a File to raw base64 (strip the "data:...;base64," prefix the DataURL adds).
const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const result = reader.result || "";
            const comma = String(result).indexOf(",");
            resolve(comma >= 0 ? String(result).slice(comma + 1) : String(result));
        };
        reader.onerror = () => reject(new Error("No se pudo leer el archivo."));
        reader.readAsDataURL(file);
    });

const formatDate = (value) => {
    if (!value) return "—";
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString("es-MX");
};

const validate = () => {
    if (!cerFile.value) return "Selecciona el archivo del certificado (.cer).";
    if (!keyFile.value) return "Selecciona el archivo de la llave privada (.key).";
    if (!password.value) return "Ingresa la contraseña de la llave privada.";
    return "";
};

const load = async () => {
    loading.value = true;
    try {
        const response = await Api.getSatCertificates();
        if (response.error) {
            return toast.error(response.message_client);
        }
        const payload = response.data;
        certificates.value = Array.isArray(payload && payload.data)
            ? payload.data
            : Array.isArray(payload)
                ? payload
                : [];
    } catch (error) {
        toast.error("No se pudieron cargar los certificados.");
    } finally {
        loading.value = false;
    }
};

const save = async () => {
    errorMessage.value = validate();
    if (errorMessage.value) return;

    saving.value = true;
    try {
        const b64Cer = await fileToBase64(cerFile.value);
        const b64Key = await fileToBase64(keyFile.value);
        const response = await Api.saveSatCertificate({
            b64Cer,
            b64Key,
            password: password.value,
        });
        if (response.error) {
            errorMessage.value = response.message_client;
            return toast.error(response.message_client);
        }
        toast.success("Certificado registrado correctamente.");
        // Clear sensitive inputs and refresh the list.
        password.value = "";
        cerFile.value = null;
        keyFile.value = null;
        if (cerInput.value) cerInput.value.value = "";
        if (keyInput.value) keyInput.value.value = "";
        await load();
    } catch (error) {
        toast.error("No se pudo registrar el certificado.");
    } finally {
        saving.value = false;
    }
};

onMounted(load);
</script>

<style scoped>
.sat-certificate {
    max-width: 800px;
    margin: auto;
}

.section-title {
    text-align: center;
    font-size: 18px;
}

.sat-certificate input[type="file"] {
    display: block;
}
</style>
